import { useEffect, useState } from 'react';
import {
  createFirstHouse,
  fetchFirstHouseStatus,
  fetchHouseAccess,
} from '../../platform/auth/session';
import type { SessionHouse, StoredSession } from '../../platform/auth/session';
import { Icon } from '../../components/ui/Icon';

export function FirstHouseOnboarding(props: {
  session: StoredSession;
  canCreateHouse: boolean;
  onEnterHouse: (house: SessionHouse) => void;
  onChooseHouses: (houses: SessionHouse[]) => void;
}) {
  const { session, canCreateHouse, onEnterHouse, onChooseHouses } = props;
  const [attempted, setAttempted] = useState(false);
  const [status, setStatus] = useState<unknown>(null);
  const [houseName, setHouseName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canCreateHouse) return;
    let active = true;
    setStatus(null);
    setAttempted(false);
    setError(null);
    void fetchFirstHouseStatus(session)
      .then((existing) => {
        if (!active) return;
        setStatus(existing);
        setAttempted(existing !== null);
      })
      .catch(() => {
        if (!active) return;
        setError(
          'Não foi possível confirmar se já existe uma tentativa anterior. Consulte o estado antes de iniciar uma nova casa.',
        );
        setAttempted(true);
      });
    return () => {
      active = false;
    };
  }, [canCreateHouse, session]);

  async function refreshStatus() {
    setBusy(true);
    setError(null);
    try {
      const current = await fetchFirstHouseStatus(session);
      setStatus(current);
      setAttempted(current !== null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível consultar o estado.');
    } finally {
      setBusy(false);
    }
  }

  async function createHouse(event: { preventDefault: () => void }) {
    event.preventDefault();
    if (!canCreateHouse || attempted) return;
    setBusy(true);
    setError(null);
    setAttempted(true);
    try {
      const result = await createFirstHouse(session, {
        displayName: displayName.trim(),
        houseName: houseName.trim(),
      });
      setStatus(result);
      if (isRecord(result) && result.ok === true) {
        try {
          const access = await fetchHouseAccess(session);
          if (access.houses.length === 1 && access.houses[0]) onEnterHouse(access.houses[0]);
          else if (access.houses.length > 1) onChooseHouses(access.houses);
          else
            setError('A casa foi criada, mas ainda não apareceu na sua lista. Consulte o estado.');
        } catch {
          setError(
            'A casa foi criada, mas não foi possível atualizar sua lista. Consulte o estado.',
          );
        }
      }
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Não foi possível confirmar o primeiro acesso.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-live="polite">
      {error ? (
        <p role="alert" className="form-error">
          {error}
        </p>
      ) : null}
      {canCreateHouse ? (
        <>
          <p>
            Seu acesso ao Zeladoria permite iniciar uma casa. Ela será criada no produto e ficará em
            configuração até que o acesso comercial seja confirmado.
          </p>
          {status ? (
            <div className="form-success">
              <p>{describeFirstHouseStatus(status).title}</p>
              <ul>
                {describeFirstHouseStatus(status).details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {!attempted ? (
            <form
              onSubmit={(event) => {
                void createHouse(event);
              }}
            >
              <div className="field">
                <label htmlFor="first-house-display-name">Seu nome nesta casa</label>
                <input
                  id="first-house-display-name"
                  value={displayName}
                  maxLength={120}
                  onChange={(event) => {
                    setDisplayName(event.target.value);
                  }}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="first-house-name">Nome da casa</label>
                <input
                  id="first-house-name"
                  value={houseName}
                  maxLength={120}
                  onChange={(event) => {
                    setHouseName(event.target.value);
                  }}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || !displayName.trim() || !houseName.trim()}
                style={{ width: '100%', minHeight: 48 }}
              >
                {busy ? 'Enviando…' : 'Criar minha primeira casa'}
                <Icon name="arrow" size={16} />
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                void refreshStatus();
              }}
              disabled={busy}
            >
              {busy ? 'Consultando…' : 'Consultar estado antes de qualquer nova tentativa'}
            </button>
          )}
        </>
      ) : (
        <p>
          Encontramos um vínculo anterior com uma casa, mas ela não aparece na sua lista. Fale com o
          suporte do Zeladoria para recuperar o acesso.
        </p>
      )}
    </section>
  );
}

function describeFirstHouseStatus(value: unknown): { title: string; details: string[] } {
  if (!isRecord(value)) {
    return {
      title: 'O processo foi enviado, mas o estado ainda não foi confirmado.',
      details: ['Consulte o estado antes de repetir a operação.'],
    };
  }
  if (value.ok === false) {
    const message =
      typeof value.errorCode === 'string' && value.errorCode.endsWith('_outcome_unknown')
        ? 'O resultado ainda está sendo confirmado pelos serviços responsáveis.'
        : 'O processo encontrou uma pendência que precisa ser tratada pela operação.';
    return { title: message, details: ['Consulte o estado antes de qualquer nova tentativa.'] };
  }
  const status =
    value.status === 'confirmed'
      ? 'Concluído'
      : value.status === 'partial'
        ? 'Parcial'
        : 'Em andamento';
  const stepLabels: Record<string, string> = {
    'structural-tenant': 'preparação da identidade estrutural',
    invitation: 'convite',
    house: 'criação da casa',
    completed: 'conclusão',
  };
  const details: string[] = [];
  if (typeof value.houseName === 'string') details.push(`Casa: ${value.houseName}`);
  if (typeof value.currentStep === 'string')
    details.push(
      `Etapa: ${stepLabels[value.currentStep] ?? 'verificação pelos serviços responsáveis'}`,
    );
  if (value.structuralTenantProvisioned === true)
    details.push('Phoenix confirmou o vínculo estrutural.');
  if (value.zeladoriaHouse === 'provisioned-set-up')
    details.push('Zeladoria criou a casa em estado inicial.');
  if (value.entitlement === 'not-confirmed')
    details.push(
      'Assinatura e liberação comercial continuam pendentes de confirmação pelo Billing.',
    );
  if (details.length === 0)
    details.push('Consulte novamente o estado informado pelos serviços responsáveis.');
  return { title: `Estado do processo: ${status}.`, details };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
